import { useCallback, useContext, useEffect, useState } from "react";
import { UserContext } from "../context/UserContext";
import { useFetchData } from "./FetchDataHelpers";

export const CAMERA_IMAGE_KINDS = {
  PREVIEW: "preview",
  DEPLOYMENT: "deployment-image",
};

export const IMAGE_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
};

// Fetches a camera image (3D preview or deployment snapshot) through the
// authenticated API and exposes it as an object URL. Images can't be loaded
// with a plain <img src> because the endpoint needs the bearer token, so the
// blob is fetched manually. The object URL is revoked whenever it's replaced
// or the component unmounts.
//
// `enabled` lets callers defer the fetch (e.g. until the card scrolls into
// view) — while false the hook stays IDLE and performs no request.
export const useCameraImage = (deploymentId, cameraId, kind, enabled = true) => {
  const { userenv } = useContext(UserContext);
  const fetchData = useFetchData();
  const [state, setState] = useState({ status: IMAGE_STATUS.IDLE, url: null });
  // Incremented to force a refetch (e.g. after capturing a new snapshot).
  const [version, setVersion] = useState(0);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let objectUrl = null;
    setState({ status: IMAGE_STATUS.LOADING, url: null });

    const load = async () => {
      const url = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${deploymentId}/cameras/${cameraId}/${kind}`;
      const { isResponseOK, data } = await fetchData(url, "GET", true);
      if (cancelled) return;
      if (isResponseOK && data) {
        objectUrl = URL.createObjectURL(data);
        setState({ status: IMAGE_STATUS.READY, url: objectUrl });
      } else {
        setState({ status: IMAGE_STATUS.ERROR, url: null });
      }
    };
    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [enabled, userenv, deploymentId, cameraId, kind, fetchData, version]);

  return { ...state, refetch };
};

// True once `ref`'s element has come within `rootMargin` of the viewport.
// Never flips back to false, so content that loaded stays loaded while the
// user scrolls past it. Environments without IntersectionObserver (jsdom)
// are treated as always visible.
export const useInViewOnce = (ref, rootMargin = "600px") => {
  const [inView, setInView] = useState(
    () => typeof IntersectionObserver === "undefined",
  );

  useEffect(() => {
    if (inView) return undefined;
    const element = ref.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, rootMargin, inView]);

  return inView;
};
